import React, { FC, useCallback } from 'react';
import { filter } from 'ramda';
import {
  makeStyles,
  Avatar,
  Link,
  Box,
  Button,
  Drawer,
  ListItem,
  List,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
  Typography,
} from '@dxb-web-ui-framework/design-system';
import { useTranslation } from 'react-i18next';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import {
  Mail as MailIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  CreditCard as CreditCardIcon,
  SwapHoriz as SwapHorizIcon,
  List as ListIcon,
  Contacts as ContactsIcon,
  Devices as DevicesIcon,
  Security as SecurityIcon,
  Loop as LoopIcon,
  Close as CloseIcon,
  // Bulk as MassPaymentsIcon,
  Logout as ExitToAppIcon,
  History as HistoryIcon,
  EN as EnglandFlagIcon,
  Cash as PayrollIcon,
} from '@dxb-web-ui-framework/design-system/dist/icons';
// import BookOutlinedIcon from '@material-ui/icons/BookOutlined';
import { UserAppTransaction } from '@dxb-web-ui-framework/iam/domain/AccessControl/entities';
import { EntitlementRestriction } from '../../../../../iam/src/components';
import { UserInfo } from '../../../../../userProfile/src/domain/UserInfo/entities';
import {
  useCorporateCustomerId,
  useSelectedCustomerId,
  useUserInfoIsUserAllowedAction,
} from '../../../../../userProfile/src/contexts/UserInfo/hooks';
import { useIsCardAdmin, useIsCardHolder } from '../../../../../transaction/src/contexts/Parameters/hooks';
import { getRoutePath } from '../../Routes/routePaths';
import { useRouteNavigatorHandler } from '../../../hooks/useRouteNavigatorHandler';

type MainMenuItem = {
  text: string;
  icon?: any;
  component?: any;
  navigation?: string;
  hidden: boolean;
  disabled?: boolean;
};

type MainMenuSection = {
  key: string;
  title: string;
  hidden: boolean;
  items: MainMenuItem[];
};

const useMainMenuItems = (customerId: string, isCardHolder: boolean): MainMenuSection[] => {
  const { t } = useTranslation();
  const isCardAdmin = useIsCardAdmin();
  const isCorporate = useCorporateCustomerId();
  const payrollListIsAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.PayrollList);
  const beneficiariesIsAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.AliasList);
  const isAccountsAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.DepositAccounts);
  const favoriteTransactionsIsAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.FavoriteTransactions);
  const isTransferHistoryAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.FundsTransferHistory);
  const isCalculatorAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.ExchangeCalculator);
  const isIndividualCardsAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.PrepaidCardList);
  const isCorporateCardsAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.CorporateCardList);
  const isCardConsentAllowed = useUserInfoIsUserAllowedAction(UserAppTransaction.CardConsent);
  const isCardsAllowed = useUserInfoIsUserAllowedAction(
    [UserAppTransaction.PrepaidCardList, UserAppTransaction.CorporateCardList],
    'OR',
  );
  return [
    {
      key: 'menu',
      hidden: isCardHolder,
      title: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Title'),
      items: filter(Boolean)([
        // {
        //   text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.Messages.Text'),
        //   icon: <MailIcon />,
        //   hidden: false,
        //   navigation: getRoutePath({ routeKey: 'messages', customerId }),
        // },
        {
          text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.Accounts.Text'),
          icon: <AccountBalanceWalletIcon />,
          hidden: !isAccountsAllowed || isCardHolder,
          navigation: getRoutePath({ routeKey: 'accounts', customerId }),
        },
        {
          text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.TransactionHistory.Text'),
          icon: <HistoryIcon />,
          hidden: !isTransferHistoryAllowed || isCardHolder,
          navigation: getRoutePath({ routeKey: 'transactions', customerId }),
        },
        // {
        //   text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.MassPayments.Text'),
        //   icon: <MassPaymentsIcon />,
        //   navigation: getRoutePath({ routeKey: 'mass-payments', customerId }),
        // },
        {
          text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.Payroll.Text'),
          icon: <PayrollIcon />,
          hidden: !payrollListIsAllowed || isCardHolder,
          navigation: getRoutePath({ routeKey: 'payroll', customerId }),
        },
        {
          text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.Authorizations.Text'),
          icon: <ListIcon />,
          hidden: isCardHolder,
          navigation: getRoutePath({ routeKey: 'authorizations', customerId }),
        },
        // {
        //   text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.RecurringOrders.Text'),
        //   icon: <CalendarIcon />,
        //   hidden: false,
        //   navigation: getRoutePath({ routeKey: 'recurring-orders', customerId }),
        // },
      ]) as MainMenuItem[],
    },
    {
      key: 'cards',
      hidden: !isCardsAllowed,
      title: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.Cards.Title'),
      items: filter(Boolean)([
        {
          text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.Cards.Text'),
          icon: <CreditCardIcon />,
          hidden: !isIndividualCardsAllowed,
          navigation: getRoutePath({ routeKey: 'cards', customerId }),
        },
        Boolean(isCorporate) && {
          text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.CorporateCards.Text'),
          icon: <CreditCardIcon />,
          hidden: !isCorporateCardsAllowed || !isCardAdmin,
          navigation: getRoutePath({ routeKey: 'corporate-cards', customerId }),
        },
        Boolean(isCorporate) && {
          text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.ConsentCard.Text'),
          icon: <CreditCardIcon />,
          hidden: !isCardConsentAllowed,
          navigation: getRoutePath({ routeKey: 'consent-card', customerId }),
        },
      ]) as MainMenuItem[],
    },
    {
      key: 'favourites',
      hidden: (!beneficiariesIsAllowed && !favoriteTransactionsIsAllowed) || isCardHolder,
      title: t('App.Header.MainMenu.Drawer.MainMenuItems.Favourites.Title'),
      items: filter(Boolean)([
        {
          text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.Beneficiaries.Text'),
          icon: <ContactsIcon />,
          hidden: !beneficiariesIsAllowed || isCardHolder,
          navigation: getRoutePath({ routeKey: 'beneficiaries', customerId }),
        },
        {
          text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.TransfersAndPayments.Text'),
          icon: <SwapHorizIcon />,
          hidden: !favoriteTransactionsIsAllowed || isCardHolder,
          navigation: getRoutePath({ routeKey: 'favorite-transfers', customerId }),
        },
      ]) as MainMenuItem[],
    },
    {
      key: 'settings',
      hidden: false,
      title: t('App.Header.MainMenu.Drawer.MainMenuItems.Settings.Title'),
      items: [
        {
          text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.Communication.Text'),
          icon: <MailIcon />,
          hidden: false,
          navigation: getRoutePath({ routeKey: 'communication-preferences', customerId }),
        },
        {
          text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.Devices.Text'),
          icon: <DevicesIcon />,
          hidden: false,
          navigation: getRoutePath({ routeKey: 'device-management', customerId }),
        },
        {
          text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.Security.Text'),
          icon: <SecurityIcon />,
          hidden: false,
          navigation: getRoutePath({ routeKey: 'security-preferences', customerId }),
        },
        // {
        //   // text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.ActivityLog.Text'),
        //   text: 'Activity log',
        //   icon: <BookOutlinedIcon />,
        //   navigation: getRoutePath({ routeKey: 'activity-log', customerId }),
        // },
        // TODO show country icon according to language
        // {
        //   text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.English.Text'),
        //   icon: <EnglandFlagIcon />,
        //   disabled: true,
        //   hidden: false,
        //   // component: <MemoizedLanguageMenu />,
        // },
      ],
    },
    {
      key: 'tools',
      hidden: !isCalculatorAllowed,
      title: t('App.Header.MainMenu.Drawer.MainMenuItems.Tools.Title'),
      items: [
        {
          text: t('App.Header.MainMenu.Drawer.MainMenuItems.Menu.Items.ExchangeCalculator.Text'),
          icon: <LoopIcon />,
          hidden: !isCalculatorAllowed,
          navigation: getRoutePath({ routeKey: 'exchange-calculator', customerId }),
        },
      ],
    },
  ];
};

type MainMenuProps = {
  isMainMenuOpen: boolean;
  onToggleMainMenu: () => void;
  userInfo: Nullable<UserInfo>;
  userInitials: string;
  onLogout: () => void;
};

const useStyles = makeStyles(theme => ({
  menu: {
    height: '100%',
    overflow: 'hidden',
    width: theme.spacing(40),
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
    },
  },
  menuBody: ({ isCardHolder }: { isCardHolder: boolean }) => ({
    paddingTop: theme.spacing(1),
    overflowY: isCardHolder ? 'auto' : 'scroll',
    height: isCardHolder ? '100%' : 'unset',
    display: isCardHolder ? 'flex' : 'unset',
    flexDirection: isCardHolder ? 'column' : 'unset',
    justifyContent: isCardHolder ? 'space-between' : 'unset',
  }),
  avatar: {
    width: theme.spacing(6),
    height: theme.spacing(6),
  },
  typography: {
    fontWeight: 500,
  },
  listMenuItem: {
    width: '100%',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  list: {
    paddingBottom: theme.spacing(2),
  },
  text: {
    fontSize: theme.typography.body2.fontSize,
  },
  link: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
}));

const MainMenu: FC<MainMenuProps> = ({ isMainMenuOpen, onToggleMainMenu, userInfo, userInitials, onLogout }) => {
  const isCardHolder = useIsCardHolder();
  const classes = useStyles({ isCardHolder });
  const selectedCustomerId = useSelectedCustomerId();
  const routeNavigatorHandler = useRouteNavigatorHandler();

  const newTransferOnClick = useCallback(() => {
    onToggleMainMenu();
    routeNavigatorHandler(getRoutePath({ routeKey: 'fund-transfer', customerId: selectedCustomerId }));
  }, [selectedCustomerId, onToggleMainMenu, routeNavigatorHandler]);

  const onClickViewProfile = useCallback(
    (event: any) => {
      event.preventDefault();
      onToggleMainMenu();
      routeNavigatorHandler(getRoutePath({ routeKey: 'user-profile', customerId: selectedCustomerId }));
    },
    [onToggleMainMenu, selectedCustomerId, routeNavigatorHandler],
  );

  const onClickNavItem = useCallback(
    (item: any) => {
      if (item.navigation) {
        onToggleMainMenu();
        routeNavigatorHandler(item.navigation);
      }
    },
    [onToggleMainMenu, routeNavigatorHandler],
  );

  const renderListItemContent = useCallback(
    (item: any) => {
      if (item.component) {
        return (
          <Box component='span' display='flex' width='100%' alignItems='center'>
            <ListItemIcon>{item.icon}</ListItemIcon>
            {item.component}
          </Box>
        );
      }
      return (
        <Box component='span' display='flex' justifyContent='center' alignItems='center' width='100%'>
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.text} classes={{ primary: classes.text }} />
        </Box>
      );
    },
    [classes],
  );

  const { t } = useTranslation();

  return (
    <Drawer anchor='right' open={isMainMenuOpen} variant='temporary' onClose={onToggleMainMenu}>
      <Box component={'aside'} role='navigation' display='flex' flexDirection='column' className={classes.menu}>
        {/* Menu Header(non-scrollable) items */}
        <Box>
          <Box p={3} pt={3} display='flex' justifyContent='space-between' alignItems={'center'}>
            <Box display='flex'>
              <Avatar
                className={classes.avatar}
                alt={`${t('App.Header.MainMenu.Drawer.Avatar.Alt')}-${userInitials}`}
              />
              <Box marginLeft={2} display='flex' flexDirection='column' justifyContent='center'>
                <Typography variant='h6'>
                  {userInfo?.firstname} {userInfo?.lastname}
                </Typography>
                <Link
                  size='small'
                  aria-label={t('App.Header.MainMenu.Drawer.Link.AriaLabel')}
                  data-testid='View-profile-link'
                  onClick={onClickViewProfile}
                  classes={{ root: classes.link }}
                >
                  {t('App.Header.MainMenu.Drawer.Link.Text')}
                </Link>
              </Box>
            </Box>
            <Button
              aria-label={t('App.Header.MainMenu.Drawer.CloseMainMenuButton.AriaLabel')}
              color='secondary'
              isIconButton
              size='small'
              title={t('App.Header.MainMenu.Drawer.CloseMainMenuButton.Title')}
              variant='text'
              data-testid='close-main-menu-button'
              onClick={onToggleMainMenu}
            >
              <CloseIcon />
            </Button>
          </Box>
          {!isCardHolder && (
            <EntitlementRestriction requiredEntitlements={UserAppTransaction.TransferOrderWithRouting}>
              <Divider />
              <Box p={3}>
                <Button
                  aria-label={t('App.Header.MainMenu.Drawer.NewTransferButton.AriaLabel')}
                  title={t('App.Header.MainMenu.Drawer.NewTransferButton.Title')}
                  color='primary'
                  size='large'
                  variant='contained'
                  data-testid='new-transfer-menu-button'
                  onClick={newTransferOnClick}
                >
                  {t('App.Header.MainMenu.Drawer.NewTransferButton.Text')}
                </Button>
              </Box>
            </EntitlementRestriction>
          )}
          <Divider />
        </Box>

        {/* Menu Body(scrollable) items */}
        <Box className={classes.menuBody}>
          <List component={'ul'} disablePadding className={classes.list}>
            {useMainMenuItems(selectedCustomerId || '', isCardHolder).map(mainMenuItem => (
              <Box component={'li'} key={mainMenuItem.key} pt={3}>
                {/* Subheader for every list */}
                {!mainMenuItem.hidden && mainMenuItem.title ? (
                  <Box pl={1} pb={2}>
                    <ListSubheader component={'div'} color='primary'>
                      <Typography variant='body2' className={classes.typography}>
                        {mainMenuItem.title}
                      </Typography>
                    </ListSubheader>
                  </Box>
                ) : null}
                {/* All items of every list */}
                <List component={'ul'} role={'menu'} disablePadding>
                  {mainMenuItem.items.map(item =>
                    !item.hidden ? (
                      <ListItem
                        component={'li'}
                        button
                        aria-label={item.text}
                        title={item.text}
                        data-testid={`${item.text}-link`}
                        key={item.text}
                        role={'menuitem'}
                        className={classes.listMenuItem}
                        onClick={() => onClickNavItem(item)}
                        disabled={item.disabled}
                      >
                        {renderListItemContent(item)}
                      </ListItem>
                    ) : null,
                  )}
                </List>
              </Box>
            ))}
          </List>
          {/* Logout item */}
          {isCardHolder ? (
            <Box>
              <Divider />
              <Box pt={2} pb={2} px={2}>
                <Button
                  aria-label={t('App.Header.MainMenu.Drawer.LogoutButton.AriaLabel')}
                  title={t('App.Header.MainMenu.Drawer.LogoutButton.Title')}
                  data-testid='logout-menu-button'
                  color='primary'
                  endIcon={<ExitToAppIcon />}
                  fullWidth
                  size='small'
                  variant='text'
                  onClick={onLogout}
                >
                  {t('App.Header.MainMenu.Drawer.LogoutButton.Text')}
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <Divider />
              <Box pt={2} pb={2} px={2}>
                <Button
                  aria-label={t('App.Header.MainMenu.Drawer.LogoutButton.AriaLabel')}
                  title={t('App.Header.MainMenu.Drawer.LogoutButton.Title')}
                  data-testid='logout-menu-button'
                  color='primary'
                  endIcon={<ExitToAppIcon />}
                  fullWidth
                  size='small'
                  variant='text'
                  onClick={onLogout}
                >
                  {t('App.Header.MainMenu.Drawer.LogoutButton.Text')}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export { MainMenu };
